import axios, { AxiosInstance, AxiosError } from 'axios';

// API設定
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
};

// APIクライアントインスタンス
const apiClient: AxiosInstance = axios.create(API_CONFIG);

// 認証トークン管理
export const setAuthToken = (token: string | null): void => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('mederi_auth_token', token);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('mederi_auth_token');
  }
};

// 初期化時にトークンを設定
const storedToken = localStorage.getItem('mederi_auth_token');
if (storedToken) {
  setAuthToken(storedToken);
}

// リクエストインターセプター
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // 認証エラーの場合はトークンをクリア
    if (error.response?.status === 401) {
      setAuthToken(null);
    }

    return Promise.reject(error);
  }
);

// APIエラーレスポンス型
export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    timestamp: string;
    details?: Record<string, unknown>;
  };
}

// 認証トークンの状態確認
export const getAuthTokenStatus = (): {
  hasToken: boolean;
  token: string | null;
  hasAuthHeader: boolean;
  authHeader: string | null;
} => {
  const token = localStorage.getItem('mederi_auth_token');
  const hasAuthHeader = !!apiClient.defaults.headers.common['Authorization'];

  return {
    hasToken: !!token,
    token: token ? token.substring(0, 10) + '...' : null,
    hasAuthHeader,
    authHeader: hasAuthHeader ? 'Bearer ***' : null
  };
};

// エラーハンドリング用ユーティリティ
export const handleApiError = (error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>): string => {
  // 認証関連エラーの詳細処理
  if (error.response?.status === 401) {
    return '認証エラーが発生しました（401 Unauthorized）\n\n原因:\n- トークンが無効または期限切れ\n- トークンが正しく設定されていない';
  }

  if (error.response?.status === 403) {
    return 'アクセス権限エラーが発生しました（403 Forbidden）\n\n原因:\n- このリソースへのアクセス権限がありません';
  }

  // サーバーエラーの詳細処理
  if (error.response?.status === 404) {
    return 'リソースが見つかりません（404 Not Found）';
  }

  if (error.response?.status === 422) {
    const errorData = error.response.data;
    const validationErrors = errorData?.errors || {};
    const errorMessages = Object.entries(validationErrors)
      .map(([field, messages]) => `- ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
      .join('\n');
    return `バリデーションエラー（422 Unprocessable Entity）\n\n${errorMessages || errorData?.message || '入力データにエラーがあります'}`;
  }

  if (error.response?.status === 500) {
    return 'サーバーエラーが発生しました（500 Internal Server Error）\n\nバックエンドのログを確認してください。';
  }

  if (error.request) {
    return 'サーバーとの通信に失敗しました\n\n原因:\n- バックエンドサーバーが起動していない\n- ネットワークの問題\n- CORS設定の問題';
  }

  return `予期しないエラーが発生しました\n\nエラーメッセージ: ${error.message}`;
};

export default apiClient;
